import React, { useCallback, useContext, useEffect, useState } from 'react';
import Card from '@mui/material/Card/Card';
import Stack from '@mui/material/Stack/Stack';
import Divider from '@mui/material/Divider/Divider';
import CardContent from '@mui/material/CardContent/CardContent';
import Skeleton from '@mui/material/Skeleton';
import CardHeader from '@mui/material/CardHeader';
import { useNavigate, useParams } from 'react-router-dom';
import OrderPreview from './Components/OrderPreview';
import { OrdersContext } from '../../services/Orders';
import Title from '../../Components/Title';
import { ID, isPast, isSchedulePending, Order, shouldBeShown } from '../../Models';
import { ShipmentsContext } from '../../services/Deliveries';
import OrderDialog from './Components/OrderDialog';
import { UserContext } from '../../services/User';
import { BotmakerChatContext } from '../../services/BotmakerChat';
import { useKanbanRerender } from '../../hooks/useKanbanRerenderer';

const cardStyle = { width: '100%', px: 0, py: 1 };
const cardHeaderStyle = { px: 2, pt: 0, pb: 0 };
const columnStyle = { maxHeight: '100%', overflow: 'auto', px: 2, pb: 3, pt: 0 };

function CustomDivider() {
    return <Divider sx={{ mb: 2 }} />;
}

function CustomSkeleton({ height }: { height: number }) {
    return (
        <Skeleton style={{ borderRadius: 5 }} variant="rectangular" width="100%" height={height} />
    );
}

const skip = (order: Order) => isPast(order);
const approvedOrderSortFunction = (a: Order, b: Order) => {
    const aIsSchedulePending = isSchedulePending(a);
    const bIsSchedulePending = isSchedulePending(b);
    if (
        (aIsSchedulePending && bIsSchedulePending) ||
        (!aIsSchedulePending && !bIsSchedulePending)
    ) {
        return a.creation_timestamp.getTime() - b.creation_timestamp.getTime();
    }
    if (aIsSchedulePending) {
        return 1;
    }
    return -1;
};

export default function Kanban() {
    const { user } = useContext(UserContext);
    const { emitted, assigned, approved, loading } = useContext(OrdersContext);
    const { shipments } = useContext(ShipmentsContext);
    const [selectedOrder, setSelectedOrder] = useState<ID | undefined>();
    const { id } = useParams();
    const navigate = useNavigate();
    const { chatState } = useContext(BotmakerChatContext);
    const openOrderDialog = useCallback(
        (orderId: string) => navigate(`/orders/${orderId}`),
        [navigate],
    );
    useKanbanRerender(emitted, user.id);

    const isIdle = false;

    const closeOrderDialog = () => navigate('/orders');

    useEffect(() => {
        if (id) {
            const newSelectedOrder = [...emitted, ...assigned, ...approved].find(
                (o) => o.id === id,
            );

            if (newSelectedOrder) {
                setSelectedOrder((prev) => (prev === undefined || prev !== id ? id : prev));
            }
        } else {
            // Clear selectedOrder when no id in URL
            if (selectedOrder) setSelectedOrder(undefined);
            if (isIdle) {
                // Cuando NO hay una orden abierta
                // Y la farmacia no toca la pantalla hace unos segundos
                // Y hay un nuevo mensaje de paciente
                // => Abrimos la conversación
                const customerId = Object.entries(chatState).find(
                    ([, chat]) => chat.state === 'new-message',
                )?.[0];

                if (!customerId) return;

                const newSelectedOrder = [...assigned, ...approved].find(
                    (o) => o.customer_id === customerId,
                );
                if (newSelectedOrder) {
                    openOrderDialog(newSelectedOrder.id);
                }
            }
        }
    }, [emitted, approved, assigned, chatState, id, isIdle, openOrderDialog, selectedOrder]);

    console.log('----------- rendering kanban ------------');
    return (
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={3}
            justifyContent="space-between"
            sx={{ height: '100%', p: 0, m: 0 }}>
            <Card sx={cardStyle}>
                <CardHeader title={<Title text="Nuevos" />} sx={cardHeaderStyle} />
                <CardContent sx={columnStyle}>
                    <CustomDivider />
                    <Stack spacing={2} direction={{ xs: 'row', sm: 'column' }}>
                        {loading && <CustomSkeleton height={70} />}
                        {emitted
                            .filter((o) => shouldBeShown(o, user.id))
                            .map((order) => (
                                <OrderPreview
                                    onClick={() => openOrderDialog(order.id)}
                                    order={order}
                                />
                            ))}
                    </Stack>
                </CardContent>
            </Card>
            <Card sx={cardStyle}>
                <CardHeader title={<Title text="Facturar" />} sx={cardHeaderStyle} />
                <CardContent sx={columnStyle}>
                    <CustomDivider />
                    <Stack spacing={2} direction={{ xs: 'row', sm: 'column' }}>
                        {loading && <CustomSkeleton height={90} />}
                        {assigned.map((order) => (
                            <OrderPreview
                                onClick={() => openOrderDialog(order.id)}
                                order={order}
                                delivery={
                                    order.delivery_id ? shipments[order.delivery_id] : undefined
                                }
                            />
                        ))}
                    </Stack>
                </CardContent>
            </Card>
            <Card sx={cardStyle}>
                <CardHeader title={<Title text="Abonados" />} sx={cardHeaderStyle} />
                <CardContent sx={columnStyle}>
                    <CustomDivider />
                    <Stack spacing={2} direction={{ xs: 'row', sm: 'column' }}>
                        {loading && <CustomSkeleton height={80} />}
                        {approved
                            .filter((order) => !skip(order))
                            .sort(approvedOrderSortFunction)
                            .map((order) => (
                                <OrderPreview
                                    onClick={() => openOrderDialog(order.id)}
                                    order={order}
                                    delivery={
                                        order.delivery_id ? shipments[order.delivery_id] : undefined
                                    }
                                />
                            ))}
                    </Stack>
                </CardContent>
            </Card>
            {selectedOrder && <OrderDialog orderId={selectedOrder} closeOrder={closeOrderDialog} />}
        </Stack>
    );
}
